import { Link } from "wasp/client/router";
import { Card, CardContent } from "../client/components/ui/card";
import { CardSectionHeader, useIsDark } from "../client/components/ui/card-section-header";
import { Button } from "../client/components/ui/button";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import { Switch } from "../client/components/ui/switch";
import { Play, Pause, Square, Clock, ExternalLink } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { useSessionTimer, formatTimerTime } from "./useSessionTimer";

export default function SessionCard() {
  const t = useTranslation();
  const isDark = useIsDark();

  // Toute la logique (timer, pause, démarrage, évaluation) vit dans useSessionTimer,
  // partagée avec le mode Lite.
  const {
    activeSession,
    isLoading,
    hasTimer,
    timeRemaining,
    elapsedSeconds,
    isPaused,
    noTimer,
    setNoTimer,
    plannedDuration,
    setPlannedDuration,
    handleOpenStartDialog,
    handlePauseResume,
    handleStopSession,
    dialogs,
  } = useSessionTimer();

  const formatTime = formatTimerTime;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">Chargement...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="h-full flex flex-col">
        <CardSectionHeader
          label={t.analytics.sessionLabel}
          isDark={isDark}
          compact
          rightContent={<Clock className="h-4 w-4 text-muted-foreground" />}
        />
        <CardContent className="px-4 pb-3 pt-2 flex-1 flex flex-col justify-center overflow-hidden">
          {activeSession && activeSession.isActive ? (
            <div className="flex flex-col gap-1">
              {/* Timer or elapsed display */}
              <div className="text-center py-1">
                {hasTimer ? (
                  <>
                    <div className="relative inline-block">
                      <div className="absolute inset-0 rounded-full bg-primary/5 blur-xl dark:bg-primary/10 pointer-events-none" />
                      <span className="relative text-4xl font-bold font-mono text-primary leading-none tracking-tight">
                        {timeRemaining !== null ? formatTime(timeRemaining) : "--:--"}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {isPaused ? t.session.paused : t.session.remaining}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="relative inline-block">
                      <div className="absolute inset-0 rounded-full bg-primary/5 blur-xl dark:bg-primary/10 pointer-events-none" />
                      <span className="relative text-4xl font-bold font-mono text-primary leading-none tracking-tight">
                        {formatTime(elapsedSeconds)}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {isPaused ? t.session.paused : t.session.elapsed}
                    </div>
                  </>
                )}
              </div>

              {/* Controls */}
              <div className="flex gap-1 justify-center mt-1">
                <Button variant="outline" size="sm" onClick={handlePauseResume} className="h-7 text-xs px-2">
                  {isPaused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
                </Button>
                <Button variant="destructive" size="sm" onClick={handleStopSession} className="h-7 text-xs px-2">
                  <Square className="h-3 w-3" />
                </Button>
              </div>

              {/* Link to session detail for note-taking */}
              <Link
                to="/session/:sessionId"
                params={{ sessionId: activeSession.id }}
                className="flex items-center justify-center gap-1 text-xs text-primary hover:underline mt-1"
              >
                <ExternalLink className="h-3 w-3" />
                {t.session.openSessionNotes}
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {/* Toggle + duration on same line */}
              <div className="flex items-center gap-2">
                <Switch
                  id="no-timer"
                  checked={noTimer}
                  onCheckedChange={setNoTimer}
                  className="scale-75 origin-left shrink-0"
                />
                <Label htmlFor="no-timer" className="text-xs text-muted-foreground cursor-pointer truncate">
                  {t.session.noTimerLabel}
                </Label>
                {!noTimer && (
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    value={plannedDuration}
                    onChange={(e) => setPlannedDuration(parseInt(e.target.value) || 120)}
                    className="h-6 text-xs w-16 ml-auto shrink-0 px-2"
                    placeholder="120"
                  />
                )}
              </div>

              <Button onClick={handleOpenStartDialog} className="w-full h-7 text-xs">
                <Play className="mr-1 h-3 w-3" />
                {t.session.start}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {dialogs}
    </>
  );
}
